import React, { useEffect, useState } from "react";
import { isAndroidApp } from "../utils/androidApp";
import { FiDownload, FiRefreshCw, FiShare, FiSmartphone, FiWifi, FiWifiOff, FiX } from "react-icons/fi";
import "./installPrompt.css";

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [requestFailed, setRequestFailed] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [applyingUpdate, setApplyingUpdate] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true || isAndroidApp();
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const recentlyDismissed = Date.now() - dismissedAt < DISMISS_DAYS * 86400000;
    setIsIos(ios);

    const onInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      if (!standalone && !recentlyDismissed) setShowInstall(true);
    };
    const onInstalled = () => { setShowInstall(false); setDeferredPrompt(null); localStorage.removeItem(DISMISS_KEY); };
    const onOnline = () => { setIsOnline(true); setRequestFailed(false); };
    const onOffline = () => { setIsOnline(false); setRequestFailed(true); };
    const onRequestFailure = () => setRequestFailed(true);
    const onRequestRestored = () => { if (navigator.onLine) setRequestFailed(false); };
    const onUpdate = () => setUpdateReady(true);

    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("app:network-failure", onRequestFailure);
    window.addEventListener("app:network-restored", onRequestRestored);
    window.addEventListener("pwa:update-available", onUpdate);

    let iosTimer;
    if (ios && !standalone && !recentlyDismissed) iosTimer = setTimeout(() => setShowInstall(true), 5000);
    return () => {
      clearTimeout(iosTimer);
      window.removeEventListener("beforeinstallprompt", onInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("app:network-failure", onRequestFailure);
      window.removeEventListener("app:network-restored", onRequestRestored);
      window.removeEventListener("pwa:update-available", onUpdate);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstall(false);
  };
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShowInstall(false); };
  const applyUpdate = async () => {
    if (applyingUpdate) return;
    setApplyingUpdate(true);
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      await registration?.update().catch(() => {});
      const waitingWorker = registration?.waiting;
      if (!waitingWorker) {
        window.location.reload();
        return;
      }
      let reloading = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      }, { once: true });
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      window.setTimeout(() => {
        if (!reloading) window.location.reload();
      }, 4000);
    } catch {
      setApplyingUpdate(false);
      window.location.reload();
    }
  };

  return (
    <div className="pwa-ui-layer" dir="rtl" aria-live="polite">
      {(!isOnline || requestFailed) && <div className="pwa-status-toast is-offline"><FiWifiOff /><div><strong>{isOnline ? "ارتباط با سرور برقرار نشد" : "اتصال اینترنت قطع است"}</strong><span>اطلاعات جدید ارسال نشده‌اند؛ پس از برقراری اتصال دوباره تلاش کنید.</span></div>{isOnline && <button type="button" onClick={() => setRequestFailed(false)}>متوجه شدم</button>}</div>}
      {isOnline && !requestFailed && updateReady && <div className="pwa-status-toast is-update"><FiRefreshCw className={applyingUpdate ? "spin" : ""} /><div><strong>نسخه جدید آماده است</strong><span>برای دریافت آخرین تغییرات برنامه را تازه کنید.</span></div><button onClick={applyUpdate} disabled={applyingUpdate}>{applyingUpdate ? "در حال بروزرسانی..." : "به‌روزرسانی"}</button></div>}
      {showInstall && <div className="pwa-prompt-overlay">
        <section className="pwa-prompt-card" role="dialog" aria-modal="false" aria-labelledby="pwa-install-title">
          <button className="pwa-close-btn" onClick={dismiss} aria-label="فعلاً نه"><FiX /></button>
          <div className="pwa-brand-info"><span className="pwa-logo-shell"><img src="/logo192.png" alt="" /></span><div><h2 id="pwa-install-title">نصب وب‌اپلیکیشن اَلِف</h2><p>دسترسی سریع‌تر، تمام‌صفحه و تجربه‌ای شبیه اپلیکیشن</p></div></div>
          <div className="pwa-install-benefits"><span><FiSmartphone /> اجرا از صفحه اصلی</span><span><FiWifi /> دسترسی بهتر در اتصال ضعیف</span></div>
          {isIos ? <div className="pwa-ios-instructions"><FiShare /><p>در Safari روی گزینه اشتراک‌گذاری بزنید و سپس <strong>افزودن به صفحه اصلی</strong> را انتخاب کنید.</p></div> : <button className="pwa-install-btn" onClick={install}><FiDownload /> نصب برنامه</button>}
          <button className="pwa-later-btn" onClick={dismiss}>فعلاً نه؛ بعداً یادآوری کن</button>
        </section>
      </div>}
    </div>
  );
}
